import { useEffect, useState } from "react";
import { ChevronDown, Music, Pause, Play, SkipBack, SkipForward, Volume2, X } from "lucide-react";
import { useSpotifyPlayer } from "@/hooks/useSpotifyPlayer";
import { beginSpotifyAuth } from "@/integrations/spotify/client";

const STORAGE_KEY = "spotify_panel_open";

interface Props {
  /** Route path to return to after OAuth completes. Defaults to current pathname. */
  returnTo?: string;
  /** Position variant. */
  position?: "default" | "top-right" | "header";
}

export default function SpotifyMusicPanel({ returnTo, position = "default" }: Props) {
  const isHeader = position === "header";
  const positionClass = isHeader
    ? "relative inline-block"
    : position === "top-right"
      ? "fixed top-20 right-4 z-40"
      : "fixed bottom-4 right-4 z-40";
  const [open, setOpen] = useState<boolean>(() => localStorage.getItem(STORAGE_KEY) === "1");

  const {
    connection,
    loading,
    ready,
    state,
    playlists,
    error,
    play,
    pause,
    next,
    prev,
    setVolume,
    transferHere,
    playPlaylist,
    disconnect,
  } = useSpotifyPlayer();
  const [volume, setLocalVolume] = useState(0.5);
  const [selectedPlaylist, setSelectedPlaylist] = useState<string>("");

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, open ? "1" : "0");
  }, [open]);

  const handleConnect = () => {
    beginSpotifyAuth(returnTo ?? window.location.pathname);
  };

  return (
    <div className={`${positionClass} ${isHeader ? "" : "w-[320px] max-w-[calc(100vw-2rem)]"}`}>
      {/* Header trigger button */}
      <button
        onClick={() => setOpen((o) => !o)}
        className={`flex items-center justify-between hover:bg-white/[0.04] transition ${
          isHeader
            ? "rounded-full px-3 py-2 bg-white/[0.06] border border-white/10 text-sm gap-2"
            : "w-full px-4 py-3"
        }`}
      >
        <div className={`flex items-center text-sm text-foreground/90 ${isHeader ? "gap-2" : "gap-2 w-full"}`}>
          <Music className="w-4 h-4" />
          <span className={isHeader ? "hidden sm:inline" : ""}>Spotify</span>
          {state?.track_name && !open && (
            <span className="text-xs text-muted-foreground truncate max-w-[140px]">
              · {state.track_name}
            </span>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 transition-transform ${open ? "" : "rotate-180"} ${isHeader ? "" : "ml-2"}`}
        />
      </button>

      {/* Dropdown / expanded panel */}
      {open && (
        <div
          className={`${
            isHeader
              ? "absolute right-0 top-full mt-2 z-50 w-[320px] max-w-[calc(100vw-1rem)]"
              : ""
          } rounded-3xl backdrop-blur-2xl bg-white/[0.06] border border-white/10 shadow-2xl overflow-hidden`}
        >
          <div className="px-4 pb-4 pt-3 space-y-3">
            {loading ? (
              <p className="text-xs text-muted-foreground">Loading…</p>
            ) : !connection ? (
              <>
                <p className="text-xs text-muted-foreground">
                  Connect your Spotify Premium account to play music while you manifest.
                </p>
                <button
                  onClick={handleConnect}
                  className="w-full px-4 py-2 rounded-full bg-[#1DB954] text-black text-sm font-medium hover:bg-[#1ed760] transition"
                >
                  Connect Spotify
                </button>
              </>
            ) : !connection.is_premium ? (
              <div className="space-y-2">
                <p className="text-xs text-muted-foreground">
                  Spotify Premium is required for in-app playback.
                </p>
                <a
                  href="https://www.spotify.com/premium/"
                  target="_blank"
                  rel="noreferrer"
                  className="block text-center w-full px-4 py-2 rounded-full bg-white/10 hover:bg-white/20 text-sm transition"
                >
                  Upgrade to Premium
                </a>
                <button
                  onClick={disconnect}
                  className="w-full text-xs text-muted-foreground hover:text-foreground/80 transition"
                >
                  Disconnect
                </button>
              </div>
            ) : (
              <>
                {/* Now playing */}
                <div className="flex items-center gap-3">
                  {state?.album_art ? (
                    <img
                      src={state.album_art}
                      alt=""
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-white/5 flex items-center justify-center">
                      <Music className="w-5 h-5 text-muted-foreground" />
                    </div>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="text-sm truncate">
                      {state?.track_name ?? (ready ? "Ready to play" : "Connecting player…")}
                    </p>
                    <p className="text-xs text-muted-foreground truncate">
                      {state?.artist_name ?? ""}
                    </p>
                  </div>
                </div>

                {/* Controls */}
                <div className="flex items-center justify-center gap-4">
                  <button
                    onClick={prev}
                    className="p-2 rounded-full hover:bg-white/10 transition"
                    aria-label="Previous"
                  >
                    <SkipBack className="w-4 h-4" />
                  </button>
                  <button
                    onClick={state?.paused === false ? pause : play}
                    className="p-3 rounded-full bg-white/10 hover:bg-white/20 transition"
                    aria-label={state?.paused === false ? "Pause" : "Play"}
                  >
                    {state?.paused === false ? (
                      <Pause className="w-4 h-4" />
                    ) : (
                      <Play className="w-4 h-4" />
                    )}
                  </button>
                  <button
                    onClick={next}
                    className="p-2 rounded-full hover:bg-white/10 transition"
                    aria-label="Next"
                  >
                    <SkipForward className="w-4 h-4" />
                  </button>
                </div>

                {/* Volume */}
                <div className="flex items-center gap-2">
                  <Volume2 className="w-4 h-4 text-muted-foreground" />
                  <input
                    type="range"
                    min={0}
                    max={1}
                    step={0.01}
                    value={volume}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value);
                      setLocalVolume(v);
                      setVolume(v);
                    }}
                    className="flex-1 accent-[#1DB954]"
                  />
                </div>

                {/* Playlist picker */}
                {playlists.length > 0 && (
                  <select
                    value={selectedPlaylist}
                    onChange={(e) => {
                      const uri = e.target.value;
                      setSelectedPlaylist(uri);
                      if (uri) playPlaylist(uri);
                    }}
                    className="w-full text-sm rounded-full bg-white/5 border border-white/10 px-3 py-2 outline-none"
                  >
                    <option value="">Choose a playlist…</option>
                    {playlists.map((p) => (
                      <option key={p.id} value={p.uri}>
                        {p.name}
                      </option>
                    ))}
                  </select>
                )}

                {!ready && (
                  <p className="text-xs text-muted-foreground text-center">
                    Initializing player…
                  </p>
                )}

                {ready && !state && (
                  <button
                    onClick={transferHere}
                    className="w-full text-xs px-3 py-2 rounded-full bg-white/5 hover:bg-white/10 transition"
                  >
                    Play here (transfer from other device)
                  </button>
                )}

                <div className="flex items-center justify-between pt-1">
                  <button
                    onClick={disconnect}
                    className="text-xs text-muted-foreground hover:text-foreground/80 transition flex items-center gap-1"
                  >
                    <X className="w-3 h-3" /> Disconnect
                  </button>
                  {error && (
                    <span className="text-xs text-red-400 truncate max-w-[160px]">{error}</span>
                  )}
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
